import { getUserProfile } from 'helpers/userProfile';
import { handleFilterStateRoute } from 'pages/NewOverview/helpers/filters';
import { getEnergyTrendsHistory } from 'pages/NewOverview/helpers/overview';
import {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { useHistory } from 'react-router-dom';
import { useToast } from '~/contexts/newToastContext';
import { useFilterContext } from '../../../contexts/FilterContext';
import { TitleGraph } from '../../../styles';
import { CardsContainerLoading, DetailsButton } from '../styles';
import { EnergyTrendsGraph } from './components/Graph';
import { parseTrendsData } from './helpers';
import { TrendsCardContainer, TrendsCardsGraphContainer } from './styles';
import { EnergyTrendsData } from './types';

interface TrendsCardProps {
  expandedSplit: boolean;
  hasEnergy: boolean;
  loading: boolean;
  order: number;
}

export function TrendsCard({
  expandedSplit,
  hasEnergy,
  loading,
  order,
}: Readonly<TrendsCardProps>): ReactElement {
  const { client, filtersDependencies, selectedOptions } = useFilterContext();
  const [profile] = useState(getUserProfile);
  const [isFiltering, setIsFiltering] = useState(false);
  const [trendsData, setTrendsData] = useState<EnergyTrendsData | undefined>();
  const history = useHistory();
  const { toast } = useToast();
  const { t } = useTranslation();

  function onClickDetails(): void {
    history.push('/analise/energia?tipo=energy',
      handleFilterStateRoute(client, selectedOptions, profile));
  }

  const getTrendsInfo = useCallback(async (): Promise<void> => {
    setIsFiltering(true);

    const data = await getEnergyTrendsHistory(Number(client), {
      stateIds: selectedOptions.state,
      unitIds: selectedOptions.group?.units,
      healthStatus: selectedOptions.healthStatus,
      thermalComfortStatus: selectedOptions.status,
    });

    if (data) {
      setTrendsData(parseTrendsData(data));
    } else {
      toast(t('erroTitulo'), t('erroBuscaDadosTendenciaEnergia'), 'error');
    }

    setIsFiltering(false);
  }, [filtersDependencies]);

  useEffect(() => {
    if (client && hasEnergy && !loading) {
      getTrendsInfo();
    }
  }, [client, hasEnergy, loading, ...filtersDependencies]);

  const generalLoading = useMemo(() => loading || isFiltering, [loading, isFiltering]);

  if (generalLoading) {
    return (
      <CardsContainerLoading style={{ order }}>
        <Skeleton
          height="360px"
          borderRadius="8px"
          className="skeleton-span"
          containerClassName="skeleton-container-span"
        />
      </CardsContainerLoading>
    );
  }

  return (
    <TrendsCardContainer
      expandedSplit={expandedSplit}
      style={{ order }}
      data-testId="energy-trends-card"
    >
      <TitleGraph>{t('tendenciaDeConsumoEnergia')}</TitleGraph>
      <TrendsCardsGraphContainer>
        <EnergyTrendsGraph
          data={trendsData}
        />
        {trendsData && (
          <DetailsButton
            type="button"
            variant="primary-ghost"
            onClick={onClickDetails}
          >
            {t('verDetalhes')}
          </DetailsButton>
        )}
      </TrendsCardsGraphContainer>
    </TrendsCardContainer>
  );
}
